import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, CheckCircle2 } from "lucide-react";

/**
 * SignaturePad — herbruikbare digitale handtekening (canvas).
 * Props:
 *   onSave(dataUrl: string)  — callback met PNG data-URL
 *   initialDataUrl (optional)
 *   height (optional, default 180)
 *   label (optional, default "Teken hier uw handtekening…")
 */
export default function SignaturePad({ onSave, initialDataUrl = "", height = 180, label = "Teken hier uw handtekening…" }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(!initialDataUrl);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = initialDataUrl;
    }
  }, [initialDataUrl]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = ("touches" in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ("touches" in e ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    setEmpty(false);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stop = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true);
  };
  const save = () => {
    if (empty) { toast.error("Zet eerst een handtekening"); return; }
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div data-testid="signature-pad">
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 relative">
        <canvas
          ref={canvasRef}
          width={600}
          height={220}
          style={{ height }}
          className="w-full rounded touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
          data-testid="signature-canvas"
        />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-slate-400 text-sm italic">{label}</span>
          </div>
        )}
      </div>
      <div className="flex justify-between mt-2 gap-2">
        <Button type="button" size="sm" variant="outline" onClick={clear} data-testid="sig-clear">
          <X className="w-3 h-3 mr-1" /> Wis
        </Button>
        <Button type="button" size="sm" onClick={save} disabled={empty} data-testid="sig-save">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Handtekening bevestigen
        </Button>
      </div>
    </div>
  );
}
